import { useState, useCallback } from 'react';
import { EntityType } from '../types';

interface EditingState {
  type: EntityType | null;
  index: number | null;
  field: string | null;
}

/**
 * Hook for managing inline editing state
 */
export const useInlineEdit = () => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editingState, setEditingState] = useState<EditingState>({
    type: null,
    index: null,
    field: null,
  });

  /**
   * Start editing a field
   */
  const startEdit = useCallback(
    (type: EntityType, index: number, field: string, currentValue: number | string) => {
      setEditingField(`${type}-${index}-${field}`);
      setEditValue(currentValue.toString());
      setEditingState({ type, index, field });
    },
    []
  );

  /**
   * Cancel editing
   */
  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue('');
    setEditingState({ type: null, index: null, field: null });
  }, []);

  /**
   * Complete editing and get the parsed value
   */
  const completeEdit = useCallback((): { value: number; isValid: boolean } => {
    // Treat empty or whitespace-only input as 0
    const trimmed = editValue.trim();
    const numValue = trimmed === '' ? 0 : parseFloat(trimmed);
    const isValid = !isNaN(numValue);

    if (isValid) {
      setEditingField(null);
      setEditValue('');
      setEditingState({ type: null, index: null, field: null });
    }

    return { value: numValue, isValid };
  }, [editValue]);

  /**
   * Complete editing for text fields
   */
  const completeEditText = useCallback((): { value: string; isValid: boolean } => {
    const trimmed = editValue.trim();
    const isValid = trimmed.length > 0;

    if (isValid) {
      setEditingField(null);
      setEditValue('');
      setEditingState({ type: null, index: null, field: null });
    }

    return { value: trimmed, isValid };
  }, [editValue]);

  /**
   * Check if a specific field is being edited
   */
  const isEditing = useCallback(
    (type: EntityType, index: number, field: string) => {
      return editingField === `${type}-${index}-${field}`;
    },
    [editingField]
  );

  /**
   * Get the current editing key
   */
  const getEditKey = useCallback(
    (type: EntityType, index: number, field: string) => {
      return `${type}-${index}-${field}`;
    },
    []
  );

  return {
    editingField,
    editValue,
    editingState,
    setEditValue,
    startEdit,
    cancelEdit,
    completeEdit,
    completeEditText,
    isEditing,
    getEditKey,
  };
};
